import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    IconButton,
    DialogActions,
    Box,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import Button from 'client/componentLibrary/Button';
import {
    selectShowCartPopup,
    closePopup,
    selectCartHasPerk,
} from '../../features/cart/cartSlice';
import { selectDonationCurrency } from '../../features/checkout/checkoutSlice';
import ShoppingCartList from './ShoppingCartList';

const useStyles = makeStyles(
    ({ breakpoints, palette, spacing, typography }) => ({
        dialogTitle: {
            padding: spacing(3, 3, 1, 3),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        dialog: {
            fontFamily: typography.fontFamily,
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                '& div[role=dialog]': {
                    margin: spacing(0.5),
                    height: '90vh',
                },
            },
            '& .addDonationButton': {
                color: palette.accent.dark,
            },
        },
        dialogActions: {
            justifyContent: 'center',
            '& .continueToCheckoutButton': {
                marginLeft: 0,
                marginInlineStart: spacing(1),
            },
        },
        title: {
            fontSize: typography.pxToRem(24),
            fontWeight: 600,
        },
        closeButton: {
            position: 'absolute',
            right: spacing(1),
            top: spacing(1),
            'html[dir=rtl] &': {
                right: 'unset',
                left: spacing(1),
            },
        },
        dialogContent: {
            padding: spacing(2, 3, 3),
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                margin: 0,
            },
        },
        actionButtons: {
            display: 'flex',
            gap: spacing(1),
            marginTop: spacing(3),
        },
        emptyCart: {
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
    }),
);

export const ShoppingCartPopup = ({
    isOpen,
    onClose,
    proceedToCheckout,
    continueShopping,
    cartHasPerk,
}) => {
    const classes = useStyles();
    const history = useHistory();

    return (
        <Dialog open={isOpen} onClose={onClose} className={classes.dialog}>
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Typography variant="h6" className={classes.title}>
                    <FormattedMessage
                        id="shoppingCart.title"
                        defaultMessage="Your Cart"
                    />
                </Typography>
                <IconButton className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {cartHasPerk && (
                    <ShoppingCartList showAdditionalAmountInput={false} />
                )}

                {!cartHasPerk && (
                    <Box className={classes.emptyCart}>
                        <Typography variant="subtitle1">
                            <FormattedMessage
                                id="shoppingCart.empty"
                                defaultMessage="Cart is empty"
                            />
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    variant="contained"
                    color="info"
                    onClick={continueShopping}
                >
                    <FormattedMessage
                        id="shoppingCart.continueShoppingButton"
                        defaultMessage="Continue Shopping"
                    />
                </Button>
                <Button
                    variant="contained"
                    className="continueToCheckoutButton"
                    color="secondary"
                    onClick={() => {
                        onClose;
                        proceedToCheckout(history);
                    }}
                >
                    <FormattedMessage
                        id="shoppingCart.proceedToCheckoutButton"
                        defaultMessage="Continue to Checkout"
                    />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ShoppingCartPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired,
    selectedCurrency: PropTypes.string,
    proceedToCheckout: PropTypes.func.isRequired,
    continueShopping: PropTypes.func.isRequired,
    cartHasPerk: PropTypes.bool,
};

const mapStateToProps = state => ({
    selectedCurrency: selectDonationCurrency(state),
    isOpen: selectShowCartPopup(state),
    cartHasPerk: selectCartHasPerk(state),
});

const mapDispatchToProps = {
    onClose: closePopup,
    continueShopping: closePopup,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShoppingCartPopup);
